/**
 * External Imports
 */
import { FC, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

/**
 * Nexus Imports
 */
import { NeuNavVertical } from '@neutron/react';
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { logListClick, ListFilters } from '../../utils/analyticsHelpers';
import { setLastPath } from '../../utils/helpers';

/**
 * Style Imports
 */
import { SideNavLabel } from './SideNav.styles';

interface SupportSideNavProps {
  type: string;
  total: number;
  noRoundCount: number;
  recentlyCompletedCount: number;
  newNursesCount: number;
  newToHCACount: number;
  newToDeptCount: number;
}

const SupportSideNav: FC<SupportSideNavProps> = ({
  type,
  total,
  noRoundCount,
  recentlyCompletedCount,
  newNursesCount,
  newToHCACount,
  newToDeptCount
}) => {
  const { csrnFilter = 'all', cnedFilter = 'all' } = useParams();

  const { logTrackingEvent } = useAnalyticsApi();

  const supportFilters = useMemo(
    () => [
      { filter: 'All', count: total },
      { filter: 'No Round', count: noRoundCount },
      { filter: 'Recently Completed', count: recentlyCompletedCount },
      { filter: 'New Nurses', count: newNursesCount },
      { filter: 'New to HCA Nurses', count: newToHCACount },
      { filter: 'New to Dept Nurses', count: newToDeptCount }
      // 'Travel Nurses'
    ],
    [
      total,
      noRoundCount,
      recentlyCompletedCount,
      newNursesCount,
      newToHCACount,
      newToDeptCount
    ]
  );

  const supportSubPath = useMemo(
    () => (type === 'csrn' ? 'csc/list' : 'cned/list'),
    [type]
  );

  const changeTab = useCallback(
    (filter: string) => {
      switch (filter) {
        case 'All':
        case 'No Round':
        case 'Recently Completed':
        case 'New Nurses':
        case 'New to HCA Nurses':
        case 'New to Dept Nurses':
          logTrackingEvent(
            logListClick(
              filter?.toLowerCase().replace(/ /g, '-') as ListFilters,
              type
            )
          );
          setLastPath(
            filter === 'All'
              ? `/support/${supportSubPath}`
              : `/support/${supportSubPath}/${filter
                  .replace(/ /g, '-')
                  .toLowerCase()}`
          );
          break;
        default:
          console.log('No event for tab filter');
      }
    },
    [type, logTrackingEvent, logListClick]
  );

  // to do: edit Links to show blue/bold when active
  return (
    <NeuNavVertical
      id="support-side-nav"
      className="h-auto"
      kind="outlined"
      label="MENU"
      menu="blue"
      mode="drawer"
      style={{
        boxShadow: 'rgba(0, 0, 0, 0.15) 0 2px 4px',
        '--nav-width-drawer': 'clamp(200px, 25vw, 205px)'
      }}
    >
      <NeuNavVertical
        unit="header"
        style={{
          padding:
            '0 calc(var(--nav-padding-x) / 1.5) calc(var(--nav-padding-y) / 0.75)'
        }}
      />
      <NeuNavVertical unit="content" style={{ overflow: 'clip' }}>
        {supportFilters &&
          supportFilters.map(({ filter, count }) => (
            <Link
              key={filter}
              to={
                filter === 'All'
                  ? `/support/${supportSubPath}`
                  : `/support/${supportSubPath}/${filter
                      .replace(/ /g, '-')
                      .toLowerCase()}`
              }
              onClick={() => changeTab(filter)}
            >
              <NeuNavVertical
                id={`Side-Nav-Support-${filter}-Link`}
                active={
                  (type === 'csrn' ? csrnFilter : cnedFilter) ===
                  filter.replace(/ /g, '-').toLowerCase()
                }
                unit="item"
              >
                <SideNavLabel
                  id={`Side-Nav-Support-${filter}-Label`}
                  className="border border-dark"
                >
                  {filter} ({count})
                </SideNavLabel>
              </NeuNavVertical>
            </Link>
          ))}
      </NeuNavVertical>
    </NeuNavVertical>
  );
};
export default SupportSideNav;
